import React from "react";
import { useLang } from "../contexts/LanguageContext";

const ABOUT_AR = `بدأت تريبيكس بسؤال :
كيف ممكن نسهل حجز التجارب السياحية والترفيهية؟`;

const ABOUT_EN = `Tripex started with a question:
How can we make booking travel and entertainment experiences effortless?`;

export default function About() {
  const { t, isAr } = useLang();

  return (
    <section id="about" className="reveal relative py-16 md:py-28 overflow-hidden" style={{ scrollMarginTop: '90px' }}>
      <style>{`
        @keyframes tripex-arrow-nudge {
          0%, 100% { translate: 0 0; }
          50%      { translate: -4px 0; }
        }
        .tripex-card-cta {
          background: #2ABFBF !important;
          background-image: none !important;
          color: #FFFFFF !important;
          font-weight: 700 !important;
          border: none !important;
          border-radius: 50px !important;
          padding: 14px 28px !important;
          cursor: pointer !important;
          transition: transform 0.2s ease !important;
          box-shadow: 0 4px 12px -4px rgba(42, 191, 191, 0.4) !important;
        }
        .tripex-card-cta:hover {
          transform: scale(1.03) !important;
          box-shadow: 0 6px 18px -4px rgba(42, 191, 191, 0.55) !important;
        }
        /* Arrow anchor — absolutely placed OUTSIDE the button so it doesn't
           shift layout. Vertically centered on the button. In LTR it sits to
           the right pointing left; in RTL the wrapper is flipped so it sits
           to the left pointing right (both aim at the button center). */
        .tripex-cta-arrow {
          position: absolute !important;
          top: 50% !important;
          inset-inline-start: calc(100% + 8px) !important;
          display: inline-flex !important;
          align-items: center !important;
          pointer-events: none !important;
          opacity: 0.9 !important;
          animation: tripex-arrow-nudge 1.5s ease-in-out infinite !important;
        }
      `}</style>
      <div className="relative z-10 mx-auto max-w-6xl px-5">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left rail */}
          <div className="lg:col-span-4 lg:sticky lg:top-32">
            <div
              className={`mb-4 font-extrabold tracking-wide ${isAr ? 'font-cairo text-xl md:text-2xl' : 'font-display-en text-base md:text-lg uppercase tracking-[0.22em]'}`}
              style={{ color: '#7C4DBE' }}
              data-testid="about-kicker"
            >
              {t("about_kicker")}
            </div>
            <div className="mt-2 inline-block">
              <div className="h-[2px] w-16 bg-gradient-to-r from-emerald via-gold to-clay rounded-full" />
            </div>
          </div>

          {/* Narrative — minimal: question + decorative divider + closer */}
          <div className="lg:col-span-8" data-testid="about-narrative" dir={isAr ? 'rtl' : 'ltr'}>
            <p
              className="text-emerald-deep text-xl md:text-3xl leading-[1.7] font-semibold whitespace-pre-line text-balance"
              style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
              data-testid="about-question"
            >
              {(isAr ? ABOUT_AR : ABOUT_EN)}
            </p>

            {/* Decorative multi-color gradient divider */}
            <div className="my-6 md:my-12 flex items-center gap-4" aria-hidden="true">
              <div
                className="h-[2px] flex-1 rounded-full"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, #51BDB0 25%, #E5DA97 55%, #C499E8 80%, transparent 100%)',
                }}
              />
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: '#C499E8', boxShadow: '0 0 0 4px rgba(196,153,232,0.18)' }}
              />
              <div
                className="h-[2px] flex-1 rounded-full"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, #C499E8 20%, #E5DA97 45%, #51BDB0 75%, transparent 100%)',
                }}
              />
            </div>

            {/* Answer line — smooth bridge into 01/02/03 cards */}
            <h3
              className="text-emerald-deep text-balance text-lg md:text-3xl"
              data-testid="about-closer-tagline"
              dir={isAr ? 'rtl' : 'ltr'}
              style={{ fontFamily: isAr ? 'Cairo' : "'Plus Jakarta Sans', sans-serif", fontWeight: 700, lineHeight: 1.65 }}
            >
              {t("about_answer")}
            </h3>

            <div className="pt-4 md:pt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className="glass rounded-2xl p-5 md:p-6 flex flex-col"
                  data-testid={`about-card-${n}`}
                  dir={isAr ? 'rtl' : 'ltr'}
                >
                  <div className="font-display-en text-xs uppercase tracking-[0.18em] text-gold-deep mb-3">
                    0{n}
                  </div>
                  <div
                    className="text-base md:text-lg font-bold text-emerald-deep mb-2 leading-snug"
                    style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
                    data-testid={`about-card-${n}-title`}
                  >
                    {t(`card_${n}_title`)}
                  </div>
                  <p
                    className="text-sm md:text-[15px] text-emerald-deep/80 leading-relaxed flex-1 mb-4"
                    style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans', overflowWrap: 'anywhere' }}
                    data-testid={`about-card-${n}-body`}
                  >
                    {t(`card_${n}_body`)}
                  </p>
                  <div className="self-start relative inline-flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        const contextMap = { 1: 'explorer', 2: 'business', 3: 'teams' };
                        window.dispatchEvent(new CustomEvent('tripex:open-dana', {
                          detail: {
                            opener: t(`dana_opener_${n}`),
                            context: contextMap[n],
                          },
                        }));
                      }}
                      data-testid={`about-card-${n}-cta`}
                      className="tripex-card-cta"
                      style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
                    >
                      {t("card_cta")}
                    </button>
                    {/* Curved arrow anchored to the OUTSIDE of the button:
                        • LTR → sits to the right, curves down-left, arrowhead points LEFT toward the button.
                        • RTL → mirrored: sits to the left, arrowhead points RIGHT toward the button. */}
                    <span
                      className="tripex-cta-arrow"
                      aria-hidden="true"
                      style={{ transform: isAr ? 'scaleX(-1) translateY(-50%)' : 'translateY(-50%)' }}
                    >
                      <svg
                        width="60"
                        height="40"
                        viewBox="0 0 60 40"
                        data-testid={`about-card-${n}-arrow`}
                      >
                        {/* Cubic curve arrives HORIZONTAL at the tip so the arrowhead
                            faces the button. Path: starts upper-right (56,6), swoops
                            down and left, ends horizontal at (14,30). Both control
                            points share y=30, forcing a purely-leftward tangent at
                            the end — that's the trick that makes the arrowhead
                            point LEFT instead of down. */}
                        <path
                          d="M 56 6 C 56 30, 34 30, 14 30"
                          stroke="#7B5EA7"
                          strokeWidth="2.6"
                          fill="none"
                          strokeLinecap="round"
                        />
                        {/* LARGE arrowhead — tip at (4,30) pointing LEFT, base 16px tall. */}
                        <polygon points="4,30 16,22 16,38" fill="#7B5EA7" />
                      </svg>
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Decorative multi-color gradient divider — symmetric closer */}
            <div className="mt-12 md:mt-20 flex items-center gap-4" aria-hidden="true">
              <div
                className="h-[2px] flex-1 rounded-full"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, #51BDB0 25%, #E5DA97 55%, #C499E8 80%, transparent 100%)',
                }}
              />
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: '#C499E8', boxShadow: '0 0 0 4px rgba(196,153,232,0.18)' }}
              />
              <div
                className="h-[2px] flex-1 rounded-full"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, #C499E8 20%, #E5DA97 45%, #51BDB0 75%, transparent 100%)',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
